// Phase 9.7.B — Staff project-requests inbox (2026-05-12 #3).
// Operator-locked 2026-05-20 inbox redesign: added Receive action for
// unrouted requests (client has no rep yet).
//
// Lists `client_project_requests` rows submitted via the portal's
// /request-project form. Reps triage with three actions:
//
//   • Receive → claims the underlying client for the current rep. Auto-
//                approves the client if still pending self-registration
//                review. Project request stays pending — rep converts
//                later. Only shown when client has no rep yet.
//   • Convert → opens a dialog that captures in-hands date (required)
//                + optional project name/event type/notes/rep override
//                + POSTs to /project-requests/:id/convert → navigates
//                straight to the new project's client detail page so
//                the rep can start adding line items.
//   • Dismiss → captures an optional reason + flips the row to dismissed.
//
// Status filter chips (pending / converted / dismissed / all). The
// "Pending" view is the default so the inbox surfaces unread work
// without an extra click.
//
// 2026-10-19 (CEO): requests can carry files the client attached and items
// picked from "Re-order something we've made before". The row shows a
// "Files · Re-order" toggle that loads them (with download links) from
// GET /project-requests/:id/attachments. The rep's email links here with
// ?request=<id>, which opens that request's attachments.
import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCheck,
  Download,
  FolderPlus,
  Hand,
  Paperclip,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { StatusPill } from "@/components/ui/StatusPill";
import { api, ApiError } from "@/lib/api";
import { timeAgo } from "@/lib/utils";

type ProjectRequestStatus = "pending" | "converted" | "dismissed";

interface ProjectRequestRow {
  id: string;
  clientId: string;
  clientName: string;
  /** When null, the request is "unrouted" — any rep can Receive it. */
  clientClientRepId: string | null;
  requestingUserId: string;
  requestingUserName: string | null;
  requestingUserEmail: string | null;
  projectName: string;
  productCategory: string | null;
  budgetRange: string | null;
  deadline: string | null;
  notes: string | null;
  status: ProjectRequestStatus;
  reviewedAt: string | null;
  reviewedBy: string | null;
  reviewedByName: string | null;
  convertedToProjectId: string | null;
  convertedToProjectNumber: number | null;
  convertedToProjectName: string | null;
  createdAt: string;
  updatedAt: string | null;
  /** Files the client attached (0 until the database step runs). */
  attachmentCount?: number;
  /** Items picked from "Re-order something we've made before". */
  reorderCount?: number;
}

interface RequestExtras {
  available: boolean;
  files: Array<{
    fileId: string;
    name: string;
    mimeType: string;
    sizeBytes: number;
    url: string | null;
  }>;
  reorderItems: Array<{
    lineItemId: string;
    name: string;
    imageUrl: string | null;
    quantity: number | null;
    lastOrderNumber: number | null;
    orderId: string | null;
  }>;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface ListResponse {
  rows: ProjectRequestRow[];
  nextCursor: { createdAt: string; id: string } | null;
}

const STATUS_FILTERS: Array<{
  value: ProjectRequestStatus | undefined;
  label: string;
}> = [
  { value: "pending", label: "Pending" },
  { value: "converted", label: "Converted" },
  { value: "dismissed", label: "Dismissed" },
  { value: undefined, label: "All" },
];

function statusTone(
  status: ProjectRequestStatus,
): "active" | "warn" | "closed" {
  // pending → peach (in your queue, action required)
  // converted → still highlighted (active) — it became a project
  // dismissed → archived look
  if (status === "converted") return "active";
  if (status === "pending") return "warn";
  return "closed";
}

export function ProjectRequestsPage() {
  const [status, setStatus] = useState<ProjectRequestStatus | undefined>(
    "pending",
  );

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    params.set("limit", "100");
    return params.toString();
  }, [status]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["project-requests", "list", queryString],
    queryFn: () => api<ListResponse>(`/project-requests?${queryString}`),
  });

  const rows = data?.rows ?? [];
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get("request");

  return (
    <div className="mx-auto w-full">
      <div className="mb-6">
        <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          Project requests
        </p>
        <h1 className="mt-2 text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink">
          Inbox
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-brown-400">
          New-project briefs submitted by portal clients via{" "}
          <span className="font-mono text-xs">/request-project</span>. Convert
          a pending request into a Project to start building the order, or
          dismiss with a reason.
        </p>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-2">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.label}
            type="button"
            onClick={() => setStatus(f.value)}
            className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide transition-colors ${
              status === f.value
                ? "bg-brown-500 text-cream-50"
                : "bg-cream-100 text-brown-400 hover:bg-cream-200"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg border border-brown-100 bg-white">
        {isLoading ? (
          <div className="p-8 text-sm text-brown-300">Loading requests…</div>
        ) : isError ? (
          <div className="p-8 text-sm text-rust-600">
            Failed to load project requests.
          </div>
        ) : rows.length === 0 ? (
          <EmptyState status={status} />
        ) : (
          <table className="w-full">
            <thead className="border-b border-[#e7e7e4] bg-[#fafaf8] text-left text-[11px] font-medium text-muted">
              <tr>
                <th className="px-6 py-3">Brief</th>
                <th className="px-6 py-3">Client</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Submitted</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brown-50">
              {rows.map((r) => (
                <RequestRow key={r.id} request={r} focused={r.id === focusId} />
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p className="mt-3 text-xs text-brown-300">
        Showing {rows.length} request{rows.length === 1 ? "" : "s"}
      </p>
    </div>
  );
}

function EmptyState({
  status,
}: {
  status: ProjectRequestStatus | undefined;
}) {
  if (status === "pending") {
    return (
      <div className="px-8 py-20 text-center">
        <div className="mx-auto h-12 w-12 rounded-md bg-cream-200" />
        <h2 className="mt-6 font-display text-2xl font-bold text-brown-500">
          Inbox zero.
        </h2>
        <p className="mx-auto mt-3 max-w-md text-sm text-brown-400">
          No pending project requests. New portal submissions land here for an
          Account Manager to convert or dismiss.
        </p>
      </div>
    );
  }
  return (
    <div className="px-8 py-16 text-center">
      <p className="text-sm text-brown-300">
        No {status ?? "matching"} requests.
      </p>
    </div>
  );
}

function RequestRow({
  request: r,
  focused = false,
}: {
  request: ProjectRequestRow;
  focused?: boolean;
}) {
  const [dialog, setDialog] = useState<"convert" | "dismiss" | null>(null);
  const extrasCount = (r.attachmentCount ?? 0) + (r.reorderCount ?? 0);
  const [showExtras, setShowExtras] = useState(focused && extrasCount > 0);
  const [receiveError, setReceiveError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const isPending = r.status === "pending";
  const isUnrouted = isPending && r.clientClientRepId === null;

  const receiveMutation = useMutation({
    mutationFn: () =>
      api(`/project-requests/${r.id}/receive`, { method: "POST" }),
    onSuccess: () => {
      setReceiveError(null);
      void queryClient.invalidateQueries({ queryKey: ["project-requests"] });
      void queryClient.invalidateQueries({ queryKey: ["sales", "dashboard"] });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | null;
        setReceiveError(
          body?.error === "already_routed"
            ? "Already claimed by another rep."
            : body?.error === "not_pending"
              ? "Request is no longer pending."
              : "Couldn't claim the request.",
        );
      } else {
        setReceiveError("Couldn't claim the request.");
      }
    },
  });

  return (
    <>
      <tr className="group transition-colors hover:bg-cream-50">
        <td className="px-6 py-4">
          <div className="font-semibold text-brown-500">{r.projectName}</div>
          <div className="mt-1 flex flex-wrap gap-2 text-xs text-brown-300">
            {r.productCategory && <span>· {r.productCategory}</span>}
            {r.budgetRange && <span>· {r.budgetRange}</span>}
            {r.deadline && <span>· need by {r.deadline}</span>}
          </div>
          {r.notes && (
            <p className="mt-2 line-clamp-2 text-xs text-brown-400">
              {r.notes}
            </p>
          )}
          {extrasCount > 0 && (
            <button
              type="button"
              onClick={() => setShowExtras((v) => !v)}
              aria-expanded={showExtras}
              className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-brown-500 underline-offset-2 hover:underline"
            >
              <Paperclip className="h-3.5 w-3.5" aria-hidden />
              {[
                r.attachmentCount
                  ? `${r.attachmentCount} file${r.attachmentCount === 1 ? "" : "s"}`
                  : null,
                r.reorderCount
                  ? `${r.reorderCount} re-order item${r.reorderCount === 1 ? "" : "s"}`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
              {showExtras ? " — hide" : " — show"}
            </button>
          )}
        </td>
        <td className="px-6 py-4 text-sm">
          <Link
            to={`/clients/${r.clientId}`}
            className="font-medium text-brown-500 underline-offset-4 hover:underline"
          >
            {r.clientName}
          </Link>
          {r.requestingUserName && (
            <div className="text-xs text-brown-300">
              {r.requestingUserName}
            </div>
          )}
          {isUnrouted && (
            <div className="mt-1 text-xs font-semibold text-rust-500">
              Unrouted — no rep yet
            </div>
          )}
        </td>
        <td className="px-6 py-4">
          <StatusPill tone={statusTone(r.status)}>{r.status}</StatusPill>
          {r.status === "converted" && r.convertedToProjectNumber && (
            <div className="mt-1 text-xs text-brown-300">
              #{r.convertedToProjectNumber}
            </div>
          )}
        </td>
        <td className="px-6 py-4 text-sm text-brown-400">
          {timeAgo(r.createdAt)}
        </td>
        <td className="px-6 py-4">
          {isPending ? (
            <div className="flex flex-col items-end gap-1">
              <div className="flex justify-end gap-2">
                {isUnrouted && (
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={receiveMutation.isPending}
                    onClick={() => receiveMutation.mutate()}
                    title="Claim this request — assigns you as the client's rep. Convert separately."
                  >
                    <Hand className="h-3.5 w-3.5" />
                    {receiveMutation.isPending ? "Claiming…" : "Receive"}
                  </Button>
                )}
                {!isUnrouted && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setDialog("convert")}
                    title="Convert into a Project"
                  >
                    <FolderPlus className="h-3.5 w-3.5" />
                    Convert
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDialog("dismiss")}
                  title="Dismiss this request"
                >
                  <X className="h-3.5 w-3.5" />
                  Dismiss
                </Button>
              </div>
              {receiveError && (
                <p className="text-xs text-rust-500">{receiveError}</p>
              )}
            </div>
          ) : (
            <div className="flex justify-end gap-1 text-xs text-brown-300">
              <CheckCheck className="h-3.5 w-3.5" />
              {r.reviewedByName ?? "Reviewed"}
            </div>
          )}
        </td>
      </tr>
      {showExtras && (
        <tr className="bg-cream-50">
          <td colSpan={5} className="px-6 pb-5 pt-1">
            <RequestExtrasPanel requestId={r.id} />
          </td>
        </tr>
      )}
      {dialog === "convert" && (
        <ConvertDialog request={r} onClose={() => setDialog(null)} />
      )}
      {dialog === "dismiss" && (
        <DismissDialog request={r} onClose={() => setDialog(null)} />
      )}
    </>
  );
}

// ─── Attachments + re-order items ────────────────────────────────────

function RequestExtrasPanel({ requestId }: { requestId: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["project-requests", "attachments", requestId],
    queryFn: () =>
      api<RequestExtras>(`/project-requests/${requestId}/attachments`),
    // Download links last an hour; refresh well before that.
    staleTime: 30 * 60 * 1000,
  });

  if (isLoading) {
    return <p className="text-sm text-brown-300">Loading attachments…</p>;
  }
  if (isError || !data) {
    return (
      <p className="text-sm text-rust-600">Couldn't load the attachments.</p>
    );
  }
  if (data.files.length === 0 && data.reorderItems.length === 0) {
    return <p className="text-sm text-brown-300">Nothing attached.</p>;
  }

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {data.reorderItems.length > 0 && (
        <div>
          <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
            Re-order
          </p>
          <ul className="space-y-2">
            {data.reorderItems.map((item) => (
              <li
                key={item.lineItemId}
                className="flex items-center gap-3 rounded-md border border-brown-100 bg-white p-2"
              >
                {item.imageUrl ? (
                  <img
                    src={item.imageUrl}
                    alt=""
                    loading="lazy"
                    className="h-12 w-12 shrink-0 rounded bg-cream-100 object-contain"
                  />
                ) : (
                  <span className="h-12 w-12 shrink-0 rounded bg-cream-100" />
                )}
                <div className="min-w-0 text-sm">
                  <div className="truncate font-semibold text-brown-500">
                    {item.name}
                  </div>
                  <div className="text-xs text-brown-400">
                    {item.quantity ? `${item.quantity} pcs` : "Quantity not given"}
                    {item.lastOrderNumber ? (
                      <>
                        {" · last on "}
                        {item.orderId ? (
                          <Link
                            to={`/orders/${item.orderId}`}
                            className="font-medium text-brown-500 underline-offset-2 hover:underline"
                          >
                            #{item.lastOrderNumber}
                          </Link>
                        ) : (
                          `#${item.lastOrderNumber}`
                        )}
                      </>
                    ) : null}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {data.files.length > 0 && (
        <div>
          <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
            Files
          </p>
          <ul className="divide-y divide-brown-50 rounded-md border border-brown-100 bg-white">
            {data.files.map((f) => (
              <li
                key={f.fileId}
                className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
              >
                <span className="min-w-0 truncate text-brown-500">
                  {f.name}
                  <span className="ml-2 text-xs text-brown-300">
                    {formatFileSize(f.sizeBytes)}
                  </span>
                </span>
                {f.url ? (
                  <a
                    href={f.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-brown-500 underline-offset-2 hover:underline"
                  >
                    <Download className="h-3.5 w-3.5" aria-hidden />
                    Download
                  </a>
                ) : (
                  <span className="shrink-0 text-xs text-brown-300">
                    Storage offline
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─── Dialogs ─────────────────────────────────────────────────────────

function DialogShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <tr>
      <td colSpan={5} className="px-0 py-0">
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-brown-500/40 px-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <div className="w-full max-w-md rounded-lg border border-brown-100 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-brown-50 px-5 py-3">
              <h2 className="font-display text-lg font-bold text-brown-500">
                {title}
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="text-brown-300 hover:text-brown-500"
                aria-label="Close dialog"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-5 py-4">{children}</div>
          </div>
        </div>
      </td>
    </tr>
  );
}

function defaultInHandsDate(deadline: string | null): string {
  if (deadline) return deadline;
  // Default to 6 weeks out — typical decoration + ship lead time.
  const d = new Date();
  d.setDate(d.getDate() + 42);
  return d.toISOString().slice(0, 10);
}

function ConvertDialog({
  request: r,
  onClose,
}: {
  request: ProjectRequestRow;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [projectName, setProjectName] = useState(r.projectName);
  const [inHandsDate, setInHandsDate] = useState(
    defaultInHandsDate(r.deadline),
  );
  const [eventType, setEventType] = useState(r.productCategory ?? "");
  const [notes, setNotes] = useState(r.notes ?? "");

  const mutation = useMutation({
    mutationFn: () =>
      api<{
        projectId: string;
        projectNumber: number;
      }>(`/project-requests/${r.id}/convert`, {
        method: "POST",
        body: JSON.stringify({
          project_name:
            projectName.trim() && projectName.trim() !== r.projectName
              ? projectName.trim()
              : undefined,
          in_hands_date: inHandsDate,
          event_type: eventType.trim() || null,
          notes: notes.trim() || null,
        }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-requests"] });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      // Land on the client detail page — the rep can scroll to Projects and
      // pick up the new project from there to start adding line items.
      navigate(`/clients/${r.clientId}`);
      onClose();
    },
  });

  const errorMsg =
    mutation.error instanceof ApiError
      ? mutation.error.status === 409
        ? "Already reviewed by another rep — refresh to see latest state."
        : mutation.error.status === 404
          ? "Request no longer exists."
          : mutation.error.status === 400
            ? "Bad input — check the in-hands date format."
            : `Convert failed (${mutation.error.status})`
      : mutation.error
        ? "Convert failed — see console"
        : null;

  return (
    <DialogShell
      title={`Convert "${r.projectName}" into a Project`}
      onClose={onClose}
    >
      <p className="text-sm text-brown-400">
        A new Project will be created under{" "}
        <span className="font-medium text-brown-500">{r.clientName}</span>.
        You'll be the rep. The original brief stays on this request for audit.
      </p>
      <div className="mt-4 space-y-4">
        <div>
          <label className="block text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
            Opportunity name
          </label>
          <Input
            className="mt-1"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            maxLength={200}
          />
        </div>
        <div>
          <label className="block text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
            In-hands date{" "}
            <span className="text-rust">*</span>
          </label>
          <Input
            type="date"
            className="mt-1"
            value={inHandsDate}
            onChange={(e) => setInHandsDate(e.target.value)}
            required
          />
        </div>
        <div>
          <label className="block text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
            Event type (optional)
          </label>
          <Input
            className="mt-1"
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            maxLength={120}
            placeholder="e.g. promo, onboarding, conference"
          />
        </div>
        <div>
          <label className="block text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
            Notes
          </label>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={4000}
            className="mt-1 w-full rounded-md border border-brown-100 bg-white px-3 py-2 text-sm text-brown-500 placeholder:text-[#8a8a85] focus:border-ink focus:outline-none focus:ring-2 focus:ring-rust/30"
          />
        </div>
      </div>
      {errorMsg && (
        <p className="mt-4 rounded-md border border-rust-300 bg-rust-50 px-3 py-2 text-sm text-rust-600">
          {errorMsg}
        </p>
      )}
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => mutation.mutate()}
          disabled={
            !projectName.trim() || !inHandsDate || mutation.isPending
          }
        >
          {mutation.isPending ? "Converting…" : "Create opportunity"}
        </Button>
      </div>
    </DialogShell>
  );
}

function DismissDialog({
  request: r,
  onClose,
}: {
  request: ProjectRequestRow;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState("");
  const mutation = useMutation({
    mutationFn: () =>
      api(`/project-requests/${r.id}/dismiss`, {
        method: "POST",
        body: JSON.stringify({
          reason: reason.trim() || null,
        }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-requests"] });
      onClose();
    },
  });

  const errorMsg =
    mutation.error instanceof ApiError
      ? mutation.error.status === 409
        ? "Already reviewed by another rep — refresh to see latest state."
        : mutation.error.status === 404
          ? "Request no longer exists."
          : `Dismiss failed (${mutation.error.status})`
      : mutation.error
        ? "Dismiss failed — see console"
        : null;

  return (
    <DialogShell title={`Dismiss "${r.projectName}"`} onClose={onClose}>
      <p className="text-sm text-brown-400">
        The request stays in the inbox under{" "}
        <span className="font-medium">Dismissed</span> with the reason you
        provide. No data is deleted — this is a "won't pursue" signal, not a
        rejection of the client.
      </p>
      <div className="mt-4">
        <label className="block text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          Reason (optional)
        </label>
        <textarea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={500}
          className="mt-1 w-full rounded-md border border-brown-100 bg-white px-3 py-2 text-sm text-brown-500 placeholder:text-[#8a8a85] focus:border-ink focus:outline-none focus:ring-2 focus:ring-rust/30"
          placeholder="e.g. duplicate of request #..., out of scope, etc."
        />
        <p className="mt-1 text-xs text-brown-300">{reason.length} / 500</p>
      </div>
      {errorMsg && (
        <p className="mt-4 rounded-md border border-rust-300 bg-rust-50 px-3 py-2 text-sm text-rust-600">
          {errorMsg}
        </p>
      )}
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="destructive"
          size="sm"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? "Dismissing…" : "Dismiss request"}
        </Button>
      </div>
    </DialogShell>
  );
}
